import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaGlobe, FaHandshake, FaShieldAlt, FaUsers, FaArrowRight } from 'react-icons/fa';
import Reveal from '../components/Reveal';
import { gridContainer, gridItem, cardHover, heroContainer, viewportOnce } from '../components/motionPresets';
import { useT } from '../i18n/LanguageContext';
import './About.css';

const T = {
    en: {
        heroTitle: 'About ElecSolution',
        heroSubtitle: 'Your trusted partner in importation, warehousing, and transit solutions',
        whoWeAreTitle: 'Who We Are',
        whoWeAreP1: "In the bustling world of global trade and online marketing, navigating the struggles of supply chain management can feel like a daunting task. Are you frustrated by sky-high costs, subpar products, and delivery delays that seem never-ending? You're not alone!",
        whoWeAreP2: "We've been there — we understand these pain points and are here to transform your experience with expert solutions in importation, transit, and warehousing. Whether you're buying for personal enjoyment or preparing to resell, we have you covered.",
        whoWeAreP3: 'Our tailored approach empowers you to source top-quality products from around the globe, manage logistics effortlessly, and store goods securely — all according to your unique needs.',
        coreValuesTitle: 'Our Core Values',
        globalReachTitle: 'Global Reach',
        globalReachText: 'We connect you to trusted suppliers worldwide, opening doors to the best products at competitive prices.',
        trustTitle: 'Trust & Reliability',
        trustText: 'We build lasting relationships based on transparency, honesty, and consistent delivery of promises.',
        qualityTitle: 'Quality Assurance',
        qualityText: 'Every product and service we offer meets strict quality standards because you deserve the best.',
        communityTitle: 'Community First',
        communityText: "We're building a vibrant community of importers, helping each other grow and succeed together.",
        missionTitle: 'Our Mission',
        missionText: 'To empower individuals and businesses to take control of their supply chains by providing expert knowledge, reliable logistics services, and access to quality products from around the world.',
        visionTitle: 'Our Vision',
        visionText: 'To become the leading supply chain solutions provider in Central Africa, making importation accessible, affordable, and efficient for everyone.',
        ctaTitle: 'Your Success Is Our Priority',
        ctaText: "Don't let the complexities of logistics hold you back any longer. Join our vibrant community and embark on a journey toward a more efficient and profitable future.",
        ctaButton: 'Get Started',
    },
    fr: {
        heroTitle: 'À propos d’ElecSolution',
        heroSubtitle: 'Votre partenaire de confiance pour l’importation, l’entreposage et les solutions de transit',
        whoWeAreTitle: 'Qui sommes-nous',
        whoWeAreP1: "Dans l’univers trépidant du commerce mondial et du marketing en ligne, gérer les difficultés de la chaîne d’approvisionnement peut sembler une tâche redoutable. Êtes-vous frustré par des coûts exorbitants, des produits de qualité médiocre et des délais de livraison interminables ? Vous n’êtes pas seul !",
        whoWeAreP2: "Nous sommes passés par là — nous comprenons ces difficultés et nous sommes là pour transformer votre expérience grâce à des solutions expertes en importation, transit et entreposage. Que vous achetiez pour votre usage personnel ou que vous prépariez une revente, nous avons ce qu’il vous faut.",
        whoWeAreP3: 'Notre approche sur mesure vous permet de vous approvisionner en produits de première qualité partout dans le monde, de gérer la logistique sans effort et de stocker vos marchandises en toute sécurité — le tout selon vos besoins spécifiques.',
        coreValuesTitle: 'Nos valeurs fondamentales',
        globalReachTitle: 'Portée mondiale',
        globalReachText: 'Nous vous mettons en relation avec des fournisseurs de confiance dans le monde entier, vous ouvrant l’accès aux meilleurs produits à des prix compétitifs.',
        trustTitle: 'Confiance et fiabilité',
        trustText: 'Nous bâtissons des relations durables fondées sur la transparence, l’honnêteté et le respect constant de nos engagements.',
        qualityTitle: 'Assurance qualité',
        qualityText: 'Chaque produit et service que nous proposons répond à des normes de qualité strictes, parce que vous méritez le meilleur.',
        communityTitle: 'La communauté avant tout',
        communityText: 'Nous construisons une communauté dynamique d’importateurs, qui s’entraident pour grandir et réussir ensemble.',
        missionTitle: 'Notre mission',
        missionText: 'Donner aux particuliers et aux entreprises les moyens de maîtriser leur chaîne d’approvisionnement en leur offrant une expertise, des services logistiques fiables et l’accès à des produits de qualité du monde entier.',
        visionTitle: 'Notre vision',
        visionText: 'Devenir le principal fournisseur de solutions de chaîne d’approvisionnement en Afrique centrale, en rendant l’importation accessible, abordable et efficace pour tous.',
        ctaTitle: 'Votre réussite est notre priorité',
        ctaText: 'Ne laissez plus la complexité de la logistique vous freiner. Rejoignez notre communauté dynamique et lancez-vous vers un avenir plus efficace et plus rentable.',
        ctaButton: 'Commencer',
    },
};

function About() {
    const t = useT(T);
    return (
        <div className="about-page">
            <motion.section className="page-hero" initial="hidden" animate="show" variants={heroContainer}>
                <motion.h1 variants={gridItem}>{t.heroTitle}</motion.h1>
                <motion.p variants={gridItem}>{t.heroSubtitle}</motion.p>
            </motion.section>

            <section className="about-intro section-container">
                <Reveal className="about-text">
                    <h2>{t.whoWeAreTitle}</h2>
                    <p>{t.whoWeAreP1}</p>
                    <p>{t.whoWeAreP2}</p>
                    <p>{t.whoWeAreP3}</p>
                </Reveal>
            </section>

            <section className="values-section">
                <div className="section-container">
                    <Reveal><h2 className="section-title">{t.coreValuesTitle}</h2></Reveal>
                    <motion.div className="values-grid" variants={gridContainer} initial="hidden" whileInView="show" viewport={viewportOnce}>
                        <motion.div className="value-card" variants={gridItem} whileHover={cardHover}>
                            <FaGlobe className="value-icon" />
                            <h3>{t.globalReachTitle}</h3>
                            <p>{t.globalReachText}</p>
                        </motion.div>
                        <motion.div className="value-card" variants={gridItem} whileHover={cardHover}>
                            <FaHandshake className="value-icon" />
                            <h3>{t.trustTitle}</h3>
                            <p>{t.trustText}</p>
                        </motion.div>
                        <motion.div className="value-card" variants={gridItem} whileHover={cardHover}>
                            <FaShieldAlt className="value-icon" />
                            <h3>{t.qualityTitle}</h3>
                            <p>{t.qualityText}</p>
                        </motion.div>
                        <motion.div className="value-card" variants={gridItem} whileHover={cardHover}>
                            <FaUsers className="value-icon" />
                            <h3>{t.communityTitle}</h3>
                            <p>{t.communityText}</p>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            <section className="mission-section section-container">
                <motion.div className="mission-grid" variants={gridContainer} initial="hidden" whileInView="show" viewport={viewportOnce}>
                    <motion.div className="mission-card" variants={gridItem} whileHover={cardHover}>
                        <h3>{t.missionTitle}</h3>
                        <p>{t.missionText}</p>
                    </motion.div>
                    <motion.div className="mission-card" variants={gridItem} whileHover={cardHover}>
                        <h3>{t.visionTitle}</h3>
                        <p>{t.visionText}</p>
                    </motion.div>
                </motion.div>
            </section>

            <section className="cta-section">
                <Reveal className="cta-content" y={36}>
                    <h2>{t.ctaTitle}</h2>
                    <p>{t.ctaText}</p>
                    <Link to="/contact" className="btn btn-primary">
                        {t.ctaButton} <FaArrowRight />
                    </Link>
                </Reveal>
            </section>
        </div>
    );
}

export default About;
